import { useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { DropZone, compressImageForAi } from './shared';

export type ProductSource = 'system' | 'manual';
export interface ManualProduct {
  name: string; medidas: string; funcao: string; diferencial: string; ambiente: string; observacao: string;
}
export const EMPTY_MANUAL: ManualProduct = { name: '', medidas: '', funcao: '', diferencial: '', ambiente: '', observacao: '' };

const PAGE = 48;

// Passo 1 das telas de Fotos/Vídeos: escolher um produto do catálogo OU enviar a foto.
export function ProductSourcePicker({
  source, onSource, products, selectedProductId, onSelectProduct,
  manual, onManual, manualImageUrl, onManualImage, manualRefs, onManualRefs,
  upload, refsUsedBy, showAmbiente = true, firstAssetLabel = 'primeira imagem',
}: {
  source: ProductSource; onSource: (s: ProductSource) => void;
  products: any[]; selectedProductId: string; onSelectProduct: (id: string) => void;
  manual: ManualProduct; onManual: (m: ManualProduct) => void;
  manualImageUrl: string; onManualImage: (url: string) => void;
  manualRefs: string[]; onManualRefs: (urls: string[]) => void;
  upload: (fd: FormData) => Promise<{ imageUrl: string }>;
  // Etapas do estilo escolhido que recebem as fotos de referência (vazio = nenhuma usa).
  refsUsedBy: string[] | null;
  showAmbiente?: boolean;
  firstAssetLabel?: string;
}) {
  const [query, setQuery] = useState('');
  const [limit, setLimit] = useState(PAGE);
  const [uploading, setUploading] = useState(false);
  const [uploadingRef, setUploadingRef] = useState(false);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return products;
    return products.filter(p => String(p.name || '').toLowerCase().includes(q) || String(p.sku || '').toLowerCase().includes(q));
  }, [products, query]);
  const selected = products.find(p => p.id === selectedProductId);

  const sendFile = async (file: File) => {
    const fd = new FormData();
    fd.append('photo', await compressImageForAi(file));
    return (await upload(fd)).imageUrl;
  };
  const uploadMain = async (file: File) => {
    setUploading(true);
    try { onManualImage(await sendFile(file)); }
    catch (e: any) { toast.error(e.message || 'Erro ao enviar foto'); }
    finally { setUploading(false); }
  };
  const uploadRef = async (file: File) => {
    setUploadingRef(true);
    try { const url = await sendFile(file); onManualRefs([...manualRefs, url]); }
    catch (e: any) { toast.error(e.message || 'Erro ao enviar referência'); }
    finally { setUploadingRef(false); }
  };

  const set = (k: keyof ManualProduct) => (e: { target: { value: string } }) => onManual({ ...manual, [k]: e.target.value });
  const fieldCls = 'mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-indigo-400 focus:outline-none focus:ring-2 focus:ring-indigo-100';
  const labelCls = 'text-xs font-semibold text-slate-600';

  const refsHint = refsUsedBy === null
    ? null
    : refsUsedBy.length
      ? <>Usadas pelas etapas: <b>{refsUsedBy.join(', ')}</b>.</>
      : <span className="text-amber-700">O estilo escolhido não usa fotos de referência — elas serão ignoradas.</span>;

  return (
    <div className="space-y-4">
      {/* Origem do produto */}
      <div className="grid gap-3 sm:grid-cols-2">
        {([
          { id: 'system', icon: '📦', title: 'Do meu catálogo', desc: 'Usa a foto e os dados já cadastrados. Dá para aplicar o resultado direto no produto e nos marketplaces.' },
          { id: 'manual', icon: '📤', title: 'Enviar uma foto', desc: 'Para produtos fora do catálogo. Você envia a foto e descreve o produto.' },
        ] as const).map(opt => {
          const active = source === opt.id;
          return (
            <button key={opt.id} type="button" onClick={() => onSource(opt.id)}
              className={`flex items-start gap-3 rounded-xl border-2 p-3 text-left transition ${active ? 'border-indigo-500 bg-indigo-50/60' : 'border-slate-200 bg-white hover:border-slate-300'}`}>
              <span className="text-2xl leading-none">{opt.icon}</span>
              <span>
                <span className={`block text-sm font-bold ${active ? 'text-indigo-700' : 'text-slate-800'}`}>{opt.title}</span>
                <span className="mt-0.5 block text-xs text-slate-500">{opt.desc}</span>
              </span>
            </button>
          );
        })}
      </div>

      {source === 'system' && (
        <div>
          {selected && (
            <div className="mb-3 flex items-center gap-3 rounded-xl border border-indigo-200 bg-indigo-50 p-2.5">
              <img src={(selected.imageUrls || [])[0]} alt={selected.name} className="h-14 w-14 flex-shrink-0 rounded-lg border border-white object-cover" />
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-bold text-indigo-900">{selected.name}</p>
                <p className="text-[11px] text-indigo-700">
                  {selected.sku ? `SKU ${selected.sku} · ` : ''}{(selected.imageUrls || []).length} foto(s) no cadastro
                  {(selected.imageUrls || []).length > 1 ? ' — as extras entram como referência' : ''}
                </p>
              </div>
              <button type="button" onClick={() => onSelectProduct('')} className="rounded-lg px-2 py-1 text-xs font-semibold text-indigo-700 hover:bg-indigo-100">Trocar</button>
            </div>
          )}
          <input value={query} onChange={e => { setQuery(e.target.value); setLimit(PAGE); }} placeholder="Buscar por nome ou SKU..."
            className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm focus:border-indigo-400 focus:outline-none focus:ring-2 focus:ring-indigo-100" />
          <div className="mt-3 grid max-h-[26rem] grid-cols-3 gap-2 overflow-y-auto pr-1 sm:grid-cols-4 xl:grid-cols-6">
            {filtered.slice(0, limit).map(p => {
              const thumb = (p.imageUrls || [])[0];
              const isSel = selectedProductId === p.id;
              return (
                <button key={p.id} type="button" disabled={!thumb} onClick={() => onSelectProduct(p.id)}
                  title={thumb ? p.name : 'Produto sem foto — adicione uma foto no cadastro ou use "Enviar uma foto".'}
                  className={`group relative overflow-hidden rounded-xl border-2 bg-white text-left transition disabled:cursor-not-allowed disabled:opacity-50 ${isSel ? 'border-indigo-500 ring-2 ring-indigo-100' : 'border-slate-200 hover:border-slate-300'}`}>
                  {thumb
                    ? <img src={thumb} alt={p.name} loading="lazy" className="aspect-square w-full object-cover" />
                    : <span className="flex aspect-square w-full items-center justify-center bg-slate-50 text-[11px] text-slate-400">sem foto</span>}
                  {isSel && <span className="absolute right-1.5 top-1.5 flex h-6 w-6 items-center justify-center rounded-full bg-indigo-600 text-xs font-bold text-white">✓</span>}
                  <span className="block p-2">
                    <span className="line-clamp-2 text-xs font-semibold text-slate-700">{p.name}</span>
                    {p.sku && <span className="block truncate text-[10px] text-slate-400">SKU {p.sku}</span>}
                  </span>
                </button>
              );
            })}
          </div>
          {filtered.length > limit && (
            <button type="button" onClick={() => setLimit(l => l + PAGE)} className="mt-2 w-full rounded-lg border border-slate-200 py-2 text-xs font-semibold text-slate-600 hover:bg-slate-50">
              Mostrar mais ({filtered.length - limit} restantes)
            </button>
          )}
          {products.length === 0 && <p className="rounded-xl border border-dashed border-slate-200 px-3 py-6 text-center text-xs text-slate-500">Nenhum produto cadastrado. Use "Enviar uma foto".</p>}
          {products.length > 0 && filtered.length === 0 && <p className="px-3 py-6 text-center text-xs text-slate-500">Nenhum produto encontrado para "{query}".</p>}
        </div>
      )}

      {source === 'manual' && (
        <div className="space-y-4">
          <div className="grid gap-4 md:grid-cols-[200px_1fr]">
            <div>
              <p className={labelCls}>Foto do produto <span className="text-rose-500">*</span></p>
              <DropZone onFile={uploadMain} busy={uploading} className="mt-1 h-48 p-2">
                {manualImageUrl
                  ? <img src={manualImageUrl} alt="produto" className="max-h-40 rounded-lg object-contain" />
                  : <span className="px-3 text-xs text-slate-500">{uploading ? 'Enviando...' : <><b className="block text-sm text-slate-700">Arraste a foto aqui</b>ou clique para escolher</>}</span>}
              </DropZone>
              {manualImageUrl && <p className="mt-1 text-center text-[11px] text-slate-400">Clique ou arraste para trocar</p>}
            </div>
            <div className="grid gap-3">
              <div className="grid gap-3 sm:grid-cols-2">
                <label className="block"><span className={labelCls}>Nome do produto <span className="text-rose-500">*</span></span>
                  <input value={manual.name} onChange={set('name')} placeholder="Ex.: Luminária de mesa em MDF" className={fieldCls} /></label>
                <label className="block"><span className={labelCls}>Medidas</span>
                  <input value={manual.medidas} onChange={set('medidas')} placeholder="Ex.: 30 x 20 x 15 cm" className={fieldCls} /></label>
              </div>
              <label className="block"><span className={labelCls}>Para que serve?</span>
                <textarea value={manual.funcao} onChange={set('funcao')} rows={2} placeholder="Ex.: iluminação indireta para escrivaninha" className={fieldCls} /></label>
              <label className="block"><span className={labelCls}>O que torna único?</span>
                <textarea value={manual.diferencial} onChange={set('diferencial')} rows={2} placeholder="Ex.: encaixe sem cola, acabamento laqueado" className={fieldCls} /></label>
              {showAmbiente && (
                <label className="block"><span className={labelCls}>Onde é usado?</span>
                  <input value={manual.ambiente} onChange={set('ambiente')} placeholder="Ex.: home office, quarto" className={fieldCls} /></label>
              )}
            </div>
          </div>

          <label className="block"><span className={labelCls}>Pedido especial para a {firstAssetLabel} <span className="font-normal text-slate-400">(opcional)</span></span>
            <textarea value={manual.observacao} onChange={set('observacao')} rows={2} placeholder="Ex.: mostrar o produto aceso, de frente" className={fieldCls} /></label>

          <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
            <p className={labelCls}>Fotos de referência <span className="font-normal text-slate-400">(opcional — material, textura, estilo, ambiente)</span></p>
            {refsHint && <p className="mt-0.5 text-[11px] text-slate-500">{refsHint}</p>}
            <div className="mt-2 flex flex-wrap gap-2">
              {manualRefs.map((url, i) => (
                <div key={url} className="relative">
                  <img src={url} alt={`referência ${i + 1}`} className="h-16 w-16 rounded-lg border border-slate-200 object-cover" />
                  <span className="absolute -left-1 -top-1 flex h-5 w-5 items-center justify-center rounded-full bg-indigo-600 text-[10px] font-bold text-white">{i + 1}</span>
                  <button type="button" onClick={() => onManualRefs(manualRefs.filter(u => u !== url))} aria-label="Remover referência"
                    className="absolute -right-1 -top-1 flex h-5 w-5 items-center justify-center rounded-full bg-rose-500 text-[10px] text-white">✕</button>
                </div>
              ))}
              <DropZone onFile={uploadRef} busy={uploadingRef} className="h-16 w-16">
                <span className="text-[10px] text-slate-500">{uploadingRef ? '...' : '+ foto'}</span>
              </DropZone>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
